/**
 * POSTransactionsPage — `/financial-services/pos`.
 *
 * Wraps `service-payment` (`GET /v1/payments?method=POS`). The screens
 * prototype rendered a card-payment form here; the live capture flow is
 * already wired through Phase 2b's CounterPaymentPage. This page focuses on
 * the inspection surface: filterable list of POS-method payments + KPI
 * counts for today's volume.
 *
 * Wire shape note: `service-payment` returns `amountMinor` (kobo) — we
 * format with `formatNaira` from `features/mail/shared/MailUI.tsx` to stay
 * consistent with Phase 2b mail screens. service-financial-services uses
 * DECIMAL strings; the two formats co-exist by design (see Phase 4b notes).
 */
import { type JSX, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CreditCard } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { getPosDailyVolumes } from '../../lib/api/financial-services';
import {
  type PaymentStatus,
  type PaymentSummary,
  listPayments,
} from '../../lib/api/payments';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { FilterBar, KPICard } from '../mail/shared/MailUI';

const STATUSES: readonly PaymentStatus[] = [
  'PENDING',
  'AUTHORISED',
  'CAPTURED',
  'VOIDED',
  'REFUNDED',
  'FAILED',
];

function todayBounds(): { from: string; to: string } {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setHours(23, 59, 59, 999);
  return { from: start.toISOString(), to: end.toISOString() };
}

export function POSTransactionsPage(): JSX.Element {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PaymentStatus | ''>('');

  const list = useQuery({
    queryKey: ['financial-services', 'pos', statusFilter],
    queryFn: () =>
      listPayments({
        method: 'POS',
        ...(statusFilter === '' ? {} : { status: statusFilter }),
        pageSize: 50,
      }),
    retry: false,
  });

  const today = useQuery({
    queryKey: ['financial-services', 'pos', 'today'],
    queryFn: () => {
      const b = todayBounds();
      return listPayments({ method: 'POS', from: b.from, to: b.to, pageSize: 1 });
    },
    retry: false,
  });

  // Daily-volume rollups — service-financial-services aggregator covering the
  // last 14 days. Renders a mini-bar chart + KPI tiles for today's totals.
  const volumes = useQuery({
    queryKey: ['financial-services', 'pos', 'daily-volumes'],
    queryFn: () => {
      const to = new Date();
      to.setHours(23, 59, 59, 999);
      const from = new Date(to);
      from.setDate(from.getDate() - 13);
      from.setHours(0, 0, 0, 0);
      return getPosDailyVolumes({ from: from.toISOString(), to: to.toISOString(), pageSize: 14 });
    },
    retry: false,
    staleTime: 60_000,
  });
  const todayKey = new Date().toISOString().slice(0, 10);
  const todayBucket = volumes.data?.items.find((b) => b.date === todayKey) ?? null;

  const denied =
    list.isError && list.error instanceof ApiError &&
    (list.error.status === 401 || list.error.status === 403);

  const filtered = useMemo<readonly PaymentSummary[]>(() => {
    const items = list.data?.items ?? [];
    const needle = search.trim().toLowerCase();
    if (needle.length === 0) return items;
    return items.filter(
      (p) =>
        (p.reference?.id?.toLowerCase().includes(needle) ?? false) ||
        p.id.toLowerCase().includes(needle),
    );
  }, [list.data, search]);

  // Compute the bar-chart scale once per render — uses the largest daily total
  // so each bar height is proportional to the period's peak day.
  const chartScale = useMemo<number>(() => {
    const items = volumes.data?.items ?? [];
    let peak = 0;
    for (const b of items) {
      const n = Number.parseFloat(b.totalAmount);
      if (Number.isFinite(n) && n > peak) peak = n;
    }
    return peak;
  }, [volumes.data]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="POS transactions"
        description="Point-of-sale captures across NIPOST + franchise terminals."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="POS today"
          value={today.isPending ? '…' : (today.data?.total ?? 0).toLocaleString('en-US')}
          hint="POS payments captured today"
          icon={CreditCard}
        />
        <KPICard
          title="POS volume today"
          value={
            volumes.isPending
              ? '…'
              : todayBucket
              ? `₦${Number.parseFloat(todayBucket.totalAmount).toLocaleString('en-NG', {
                  maximumFractionDigits: 2,
                })}`
              : '₦0'
          }
          hint="POS volume captured today"
        />
        <KPICard
          title="POS volume — period"
          value={
            volumes.isPending
              ? '…'
              : volumes.data
              ? `₦${Number.parseFloat(volumes.data.periodTotal).toLocaleString('en-NG', {
                  maximumFractionDigits: 2,
                })}`
              : '—'
          }
          hint="last 14 days"
        />
        <KPICard
          title="Period txns"
          value={
            volumes.isPending
              ? '…'
              : (volumes.data?.periodTxnCount ?? 0).toLocaleString('en-NG')
          }
          hint="last 14 days"
        />
      </div>

      {/* Daily-volume mini-bar chart — live via service-financial-services. */}
      <section
        className="bg-white border border-border rounded-xl p-5"
        data-testid="pos-daily-volumes"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-gray-900">Daily POS volume</h3>
            <p className="text-xs text-muted">
              POS volume by day, last 14 days
            </p>
          </div>
        </div>
        {volumes.isPending ? (
          <p className="text-sm text-muted">Loading volumes…</p>
        ) : volumes.isError ? (
          <DeferredNotice title="Daily-volume aggregator unreachable">
            {(volumes.error as Error).message}
          </DeferredNotice>
        ) : (volumes.data?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-muted py-4 text-center">
            No POS volume in the selected window.
          </p>
        ) : (
          <>
            {/* SVG mini-bar chart — no external dependency. Each bar shows the
                day's total and a stacked breakdown (CASH/POS/TRANSFER). */}
            <div className="flex items-end gap-1.5 h-32 mb-2">
              {[...(volumes.data?.items ?? [])]
                .sort((a, b) => (a.date < b.date ? -1 : 1))
                .map((b) => {
                  const total = Number.parseFloat(b.totalAmount);
                  const heightPct =
                    chartScale > 0 ? Math.max(2, (total / chartScale) * 100) : 2;
                  const cashPct =
                    total > 0 ? (Number.parseFloat(b.byMethod.CASH.totalAmount) / total) * 100 : 0;
                  const posPct =
                    total > 0 ? (Number.parseFloat(b.byMethod.POS.totalAmount) / total) * 100 : 0;
                  const transferPct = Math.max(0, 100 - cashPct - posPct);
                  return (
                    <div
                      key={b.date}
                      className="flex-1 flex flex-col items-center gap-1"
                      title={`${b.date} — ₦${total.toLocaleString('en-NG')} (${b.txnCount} txns)`}
                    >
                      <div
                        className="w-full rounded-sm overflow-hidden flex flex-col-reverse bg-gray-100"
                        style={{ height: `${heightPct}%` }}
                      >
                        <div className="bg-amber-400" style={{ height: `${cashPct}%` }} />
                        <div className="bg-nipost-green" style={{ height: `${posPct}%` }} />
                        <div className="bg-blue-500" style={{ height: `${transferPct}%` }} />
                      </div>
                      <p className="text-[9px] text-muted">{b.date.slice(5)}</p>
                    </div>
                  );
                })}
            </div>
            <div className="flex items-center gap-4 text-[10px] text-muted">
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 rounded-sm bg-amber-400" /> CASH
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 rounded-sm bg-nipost-green" /> POS
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 rounded-sm bg-blue-500" /> TRANSFER
              </span>
            </div>
            {/* Tabular breakdown — last 7 days, newest first. */}
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="text-left py-1.5">Date</th>
                    <th className="text-right py-1.5">Txns</th>
                    <th className="text-right py-1.5">Total</th>
                    <th className="text-right py-1.5">CASH</th>
                    <th className="text-right py-1.5">POS</th>
                    <th className="text-right py-1.5">TRANSFER</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {volumes.data?.items.slice(0, 7).map((b) => (
                    <tr key={b.date}>
                      <td className="py-1.5 font-mono">{b.date}</td>
                      <td className="py-1.5 text-right">{b.txnCount.toLocaleString('en-NG')}</td>
                      <td className="py-1.5 text-right font-medium">
                        ₦{Number.parseFloat(b.totalAmount).toLocaleString('en-NG', { maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-1.5 text-right text-amber-700">
                        ₦{Number.parseFloat(b.byMethod.CASH.totalAmount).toLocaleString('en-NG', { maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-1.5 text-right text-nipost-green">
                        ₦{Number.parseFloat(b.byMethod.POS.totalAmount).toLocaleString('en-NG', { maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-1.5 text-right text-blue-700">
                        ₦{Number.parseFloat(b.byMethod.TRANSFER.totalAmount).toLocaleString('en-NG', { maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by reference or id…"
      >
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as PaymentStatus | '')}
          className="px-3 py-2 border border-border rounded-lg text-sm"
          aria-label="Status filter"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </FilterBar>

      {list.isPending ? (
        <p className="text-sm text-muted">Loading payments…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Payment/read</code> required.
        </DeferredNotice>
      ) : list.isError ? (
        <DeferredNotice title="Service temporarily unavailable">
          {(list.error as Error).message}
        </DeferredNotice>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          No POS transactions match the filter.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">Id</th>
                <th className="text-left px-4 py-2">Reference</th>
                <th className="text-right px-4 py-2">Amount</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-[10px] text-gray-900">{p.id}</td>
                  <td className="px-4 py-2 font-mono text-xs text-muted">{p.reference?.id ?? '—'}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    ₦{p.amountAuthorized}
                  </td>
                  <td className="px-4 py-2">
                    <StatusBadge status={p.status} />
                  </td>
                  <td className="px-4 py-2 text-xs text-muted">
                    {new Date(p.initiatedAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
